import { useEffect, useMemo, useState } from "react";
import { Search, Columns3, ListFilter, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

// ---------------------------------------------------------------
// TableToolbar
// Reusable toolbar with search, saved-view chips, column chooser.
// props:
//  - searchValue, onSearchChange, searchPlaceholder
//  - views: [{ key, label, count? }], activeView, onViewChange
//  - columns: [{ key, label, alwaysOn?: bool }], visibleColumns (Set<string>), onColumnsChange
//  - persistKey: string (localStorage key to persist visibleColumns)
// ---------------------------------------------------------------
export const useVisibleColumns = (persistKey, allColumns) => {
  const initial = useMemo(() => {
    try {
      const raw = localStorage.getItem(`col_prefs_${persistKey}`);
      if (raw) {
        const list = JSON.parse(raw);
        if (Array.isArray(list) && list.length) return new Set(list);
      }
    } catch { /* ignore */ }
    return new Set(allColumns.map((c) => c.key));
  }, [persistKey, allColumns]);
  const [visible, setVisible] = useState(initial);
  useEffect(() => {
    localStorage.setItem(`col_prefs_${persistKey}`, JSON.stringify(Array.from(visible)));
  }, [persistKey, visible]);
  const toggle = (k) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };
  return [visible, toggle];
};

export default function TableToolbar({
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "Search…",
  views = [],
  activeView,
  onViewChange,
  columns = [],
  visibleColumns,
  onToggleColumn,
  right,
  testIdPrefix = "table",
}) {
  return (
    <div className="space-y-3">
      {/* Saved views (chips) */}
      {views.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <ListFilter size={13} className="text-[#667085]" />
          {views.map((v) => (
            <button
              key={v.key}
              onClick={() => onViewChange?.(v.key)}
              data-testid={`${testIdPrefix}-view-${v.key}`}
              className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] font-semibold transition-colors ${
                activeView === v.key
                  ? "bg-[#1d3557] text-white"
                  : "bg-white text-[#457b9d] border border-[#E4E7EC] hover:border-[#457b9d] hover:bg-[#F1FAEE]"
              }`}
            >
              {v.label}
              {typeof v.count === "number" && (
                <span
                  className={`text-[10px] font-bold px-1 rounded ${
                    activeView === v.key ? "bg-white/20" : "bg-[#F1FAEE] text-[#1d3557]"
                  }`}
                >
                  {v.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Row 2: search + column chooser + right slot */}
      <div className="flex flex-wrap items-center gap-2">
        {typeof searchValue === "string" && (
          <div className="relative flex-1 max-w-md min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085]" />
            <Input
              data-testid={`${testIdPrefix}-search`}
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9 h-9 rounded-md border-[#E4E7EC] bg-white text-[13px]"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => onSearchChange?.("")}
                data-testid={`${testIdPrefix}-search-clear`}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-[#667085] hover:text-[#1d3557]"
              >
                <X size={12} />
              </button>
            )}
          </div>
        )}

        {columns.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`${testIdPrefix}-column-chooser`}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[13px] font-semibold text-[#1d3557] hover:border-[#457b9d]"
              >
                <Columns3 size={14} />
                <span className="hidden sm:inline">Columns</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 rounded-lg bg-white border border-[#E4E7EC] shadow-lg p-1"
            >
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">
                Show columns
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#E4E7EC]" />
              {columns.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.key}
                  data-testid={`${testIdPrefix}-col-${c.key}`}
                  checked={c.alwaysOn || visibleColumns.has(c.key)}
                  disabled={c.alwaysOn}
                  onCheckedChange={() => !c.alwaysOn && onToggleColumn?.(c.key)}
                  className="text-[13px] cursor-pointer"
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <div className="ml-auto flex items-center gap-2">{right}</div>
      </div>
    </div>
  );
}
